"use client"

import Script from "next/script"
import ReactDOM from "react-dom"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { useIsClient } from "src/components/hooks/useIsClient"
import { shortHash } from "src/utilities/hash"
import { useCcm19EmbeddingAccepted } from "../ccm19/useConsented"
import { integrationDebug } from "./api"
import { integrationConsentId } from "./config"
import { setIntegrationLoadState } from "./loaded"
import { useIntegrationNeeded } from "./needed"
import { IntegrationId } from "./types"

export type IntegrationScriptProps = {
  id: IntegrationId
  src: string
  onlyWhenNeeded?: boolean
  autoConsent?: boolean
  onLoad?: () => void
  [key: string]: any
}

export const IntegrationScript = ({
  id: integrationId,
  src,
  onlyWhenNeeded,
  autoConsent,
  onLoad,
  ...attributes
}: IntegrationScriptProps) => {
  const consentName = integrationConsentId[integrationId]
  const consented = useCcm19EmbeddingAccepted(consentName)
  const needed = useIntegrationNeeded(integrationId)
  const isClient = useIsClient()
  const srcHash = shortHash(src)

  const onScriptLoad = useEventCallback((event: unknown) => {
    integrationDebug(integrationId, "loaded", event)
    setIntegrationLoadState(integrationId, "loaded")
    onLoad?.()
  })

  const onScriptError = useEventCallback((event: unknown) => {
    integrationDebug(integrationId, "errored", event)
    setIntegrationLoadState(integrationId, "errored")
  })

  const scriptId = integrationId + "_" + srcHash
  const preload = autoConsent && !onlyWhenNeeded

  if (preload) {
    ReactDOM.preload(src, { as: "script" })
  }

  if (!isClient) {
    return null
  }

  if (!consented && !autoConsent) {
    return null
  }

  if (onlyWhenNeeded && !needed) {
    return null
  }

  return (
    <Script
      id={scriptId}
      src={src}
      async
      referrerPolicy="origin"
      strategy="lazyOnload"
      {...attributes}
      onLoad={onScriptLoad}
      onError={onScriptError}
    />
  )
}
